import { Platform } from "../../types/ad-platforms";
import {
  PublisherPlatforms,
  FacebookPlacementPositions,
  InstagramPlacementPositions,
  MessengerPlacementPositions,
  AudienceNetworkPlacementPositions,
  FacebookPlacementSettings
} from "./types";

export const PlatformPlacementKeys: Record<PublisherPlatforms, keyof Omit<FacebookPlacementSettings, "platforms">> = {
  [PublisherPlatforms.FACEBOOK]: "facebook_positions",
  [PublisherPlatforms.INSTAGRAM]: "instagram_positions",
  [PublisherPlatforms.AUDIENCE_NETWORK]: "audience_network_positions",
  [PublisherPlatforms.MESSENGER]: "messenger_positions"
};

// Display names map (keeping the same structure but using enums)
export const PlacementDisplayNames: Record<string, string> = {
  // Facebook
  [`${PublisherPlatforms.FACEBOOK}.${FacebookPlacementPositions.FEED}`]: "Facebook Feed",
  [`${PublisherPlatforms.FACEBOOK}.${FacebookPlacementPositions.RIGHT_HAND_COLUMN}`]: "Facebook Right Hand Column",
  [`${PublisherPlatforms.FACEBOOK}.${FacebookPlacementPositions.MARKETPLACE}`]: "Facebook Marketplace",
  [`${PublisherPlatforms.FACEBOOK}.${FacebookPlacementPositions.VIDEO_FEEDS}`]: "Facebook Video Feeds",
  [`${PublisherPlatforms.FACEBOOK}.${FacebookPlacementPositions.STORY}`]: "Facebook Story",
  [`${PublisherPlatforms.FACEBOOK}.${FacebookPlacementPositions.SEARCH}`]: "Facebook Search",
  [`${PublisherPlatforms.FACEBOOK}.${FacebookPlacementPositions.INSTREAM_VIDEO}`]: "Facebook Instream Video",
  [`${PublisherPlatforms.FACEBOOK}.${FacebookPlacementPositions.FACEBOOK_REELS}`]: "Facebook Reels",
  [`${PublisherPlatforms.FACEBOOK}.${FacebookPlacementPositions.FACEBOOK_REELS_OVERLAY}`]: "Facebook Reels Overlay",
  [`${PublisherPlatforms.FACEBOOK}.${FacebookPlacementPositions.BIZ_DISCO_FEED}`]: "Facebook Business Feed",

  // Instagram
  [`${PublisherPlatforms.INSTAGRAM}.${InstagramPlacementPositions.STREAM}`]: "Instagram Stream",
  [`${PublisherPlatforms.INSTAGRAM}.${InstagramPlacementPositions.STORY}`]: "Instagram Story",
  [`${PublisherPlatforms.INSTAGRAM}.${InstagramPlacementPositions.EXPLORE}`]: "Instagram Explore",
  [`${PublisherPlatforms.INSTAGRAM}.${InstagramPlacementPositions.EXPLORE_HOME}`]: "Instagram Explore Home",
  [`${PublisherPlatforms.INSTAGRAM}.${InstagramPlacementPositions.REELS}`]: "Instagram Reels",
  [`${PublisherPlatforms.INSTAGRAM}.${InstagramPlacementPositions.PROFILE_FEED}`]: "Instagram Profile Feed",
  [`${PublisherPlatforms.INSTAGRAM}.${InstagramPlacementPositions.IG_SEARCH}`]: "Instagram Search",
  [`${PublisherPlatforms.INSTAGRAM}.${InstagramPlacementPositions.PROFILE_REELS}`]: "Instagram Profile Reels",

  // Messenger
  [`${PublisherPlatforms.MESSENGER}.${MessengerPlacementPositions.MESSENGER_HOME}`]: "Messenger Home",
  [`${PublisherPlatforms.MESSENGER}.${MessengerPlacementPositions.SPONSORED_MESSAGES}`]: "Messenger Sponsored Messages",
  [`${PublisherPlatforms.MESSENGER}.${MessengerPlacementPositions.STORY}`]: "Messenger Story",

  // Audience Network
  [`${PublisherPlatforms.AUDIENCE_NETWORK}.${AudienceNetworkPlacementPositions.CLASSIC}`]: "Audience Network Classic",
  [`${PublisherPlatforms.AUDIENCE_NETWORK}.${AudienceNetworkPlacementPositions.REWARDED_VIDEO}`]:
    "Audience Network Rewarded Video"
};

export const PlatformDisplayNames: Record<PublisherPlatforms, string> = {
  [PublisherPlatforms.FACEBOOK]: "Facebook",
  [PublisherPlatforms.INSTAGRAM]: "Instagram",
  [PublisherPlatforms.AUDIENCE_NETWORK]: "Audience Network",
  [PublisherPlatforms.MESSENGER]: "Messenger"
};

export type PositionType =
  | FacebookPlacementPositions
  | InstagramPlacementPositions
  | MessengerPlacementPositions
  | AudienceNetworkPlacementPositions;

//export type PositionsMap = Omit<FacebookPlacementSettings, "platforms">
export type PositionsMap = {
  [PublisherPlatforms.FACEBOOK]: FacebookPlacementPositions[];
  [PublisherPlatforms.INSTAGRAM]: InstagramPlacementPositions[];
  [PublisherPlatforms.MESSENGER]: MessengerPlacementPositions[];
  [PublisherPlatforms.AUDIENCE_NETWORK]: AudienceNetworkPlacementPositions[];
};

const AdAssetFieldDisplayNameMap: Record<string, string> = {
  google_descriptions: "Google Descriptions",
  google_long_headline: "Google Long Headline",
  google_headlines: "Google Headlines",
  google_call_to_action_type: "Google Call To Action Type",
  facebook_headlines: "Meta Headlines",
  facebook_description: "Meta Description",
  facebook_bodies: "Meta Primary Texts",
  facebook_call_to_action_type: "Meta Call To Action Type",
  facebook_placement_settings: "Meta Placement Settings",
  msan_descriptions: "MSAN Descriptions",
  msan_long_headline: "MSAN Long Headline",
  msan_headlines: "MSAN Headlines",
  msan_call_to_action_type: "MSAN Call To Action Type"
};

export function AdAssetFieldDisplayName(field: string) {
  return AdAssetFieldDisplayNameMap[field] || field;
}

export const platformDisplayNames: Record<Platform, string> = {
  google: "GDN",
  meta: "Meta",
  msan: "MSAN",
  pinterest: "Pinterest",
  reddit: "Reddit",
  tiktok: "TikTok"
};

/**
 * Whether an assembling ad asset group is still waiting on its ad assets to be
 * created — the only window in which a listing tile has nothing real to show
 * and should fall back to the loading animation.
 *
 * A group can sit in `assembling` well after every asset exists (the flip to
 * ready-for-review is gated on image expansion and on the batch creator not
 * being internal), so status alone would leave the animation up indefinitely
 * over assets the user could already be looking at. `assemblingAssetCount` is
 * the group's expected asset total while assembling (the backend's
 * `assembling_ad_count`, surfaced as the `assembling` status count).
 */
export const isAwaitingAssemblyAssets = (status: string, adAssetCount: number, assemblingAssetCount: number): boolean =>
  status === "assembling" && (adAssetCount === 0 || adAssetCount < assemblingAssetCount);
